/**
 * What a deployment says about the speech model it is serving.
 *
 * A host cannot discover this for itself. Only some of a model release's files
 * are served — the directory is roughly three times the size of the subset the
 * recogniser loads, and the unused high-precision encoder must never go over the
 * wire — and which files those are is `selectModelFiles`' decision, made at build
 * time against the release on disk. So the build states it and the host reads it.
 *
 * A manifest rather than a fixed set of canonical URLs, for two reasons that both
 * outlive the ticket that wrote it. The BPE vocabulary is genuinely optional —
 * without it the recogniser drops to unbiased greedy decoding rather than
 * failing — and "absent" has to be distinguishable from "the request failed",
 * which a 404 is not. And the total is what lets the download size be stated
 * *before* it starts, which cannot be answered by asking for the bytes.
 *
 * Shared between the build script that writes it and every host that reads it, so
 * they cannot disagree about its shape. It lives here, beside the loader, because
 * the phone reads the same manifest off cuekards.com that the tab does; where
 * each host *serves* or *keeps* those files is the host's own business and is
 * named in the host's own package.
 */

/** The roles the WebAssembly recogniser's `ModelBlobs` is made of. Named here
 * rather than imported so this module stays type-only in both directions — and
 * so this package needs no opinion about the recogniser at all. */
export type ModelRole = "encoder" | "decoder" | "joiner" | "tokens" | "bpeVocab";

/** Something large that has to come over the wire, and how large it is. */
export interface ModelAsset {
  /** Where to fetch it. Relative to the site root as the build writes it, and
   * made absolute by whichever host reads the manifest — see the loader's
   * `manifest` port, which owns that resolution. */
  url: string;
  bytes: number;
}

export interface ModelFileEntry extends ModelAsset {
  role: ModelRole;
}

export interface ModelManifest {
  /** The release the files came from, for a person reading the manifest and for
   * a cache that has to be invalidated when the model changes. */
  model: string;
  /** Every file that has to be fetched before a recogniser can be built. */
  files: ModelFileEntry[];
  /**
   * The WebAssembly binary, which is a third of the browser's download and was
   * not in this manifest until web ticket 15 needed a number to put on a button.
   *
   * The recogniser is nineteen megabytes of compiled ONNX runtime, and until that
   * ticket the Emscripten loader fetched it for itself. That was fine while the
   * download was triggered by a script load and nobody was being shown a figure,
   * and it is not fine now: a size that quoted the model alone would understate
   * the download by a third, and a progress bar that reached 100% with nineteen
   * megabytes still to come would not be real progress. So it is fetched the same
   * way the model is — through the same store, with the same length check and the
   * same cancel.
   *
   * The loader *script* beside it is not here. It is a hundred and fifty
   * kilobytes, it arrives by `importScripts` because that is what keeps
   * `'unsafe-eval'` out of the page's content security policy (ADR-0007), and
   * bytes that arrive by `importScripts` cannot be counted or cancelled. It is
   * served and cached as the JavaScript it is, and the figure on the button is
   * understated by 0.2% as a result.
   *
   * A host with no WebAssembly step downloads it anyway, because it is what the
   * deployment publishes and the manifest is one shape rather than one per host.
   * Nineteen megabytes is a real cost, and the ticket that gives the phone its own
   * manifest is the place to stop paying it — not a second shape here.
   */
  runtime: ModelAsset;
}

/**
 * Everything that has to be fetched before a recogniser can be built, in the
 * order it is fetched.
 *
 * The runtime first, deliberately. Compiling nineteen megabytes of WebAssembly is
 * compute-bound and downloading forty of model is bandwidth-bound, so the two
 * overlap — and they can only overlap if the binary is the first thing to arrive.
 */
export function modelAssets(manifest: ModelManifest): ModelAsset[] {
  return [manifest.runtime, ...manifest.files];
}

/** What some set of assets weighs. The download panel quotes this of whatever is
 * still missing, which is why it takes the assets rather than the manifest. */
export function totalBytes(assets: readonly ModelAsset[]): number {
  return assets.reduce((total, asset) => total + asset.bytes, 0);
}

/** What a first visit costs, in bytes — the figure quoted before anything
 * starts. */
export function modelDownloadSize(manifest: ModelManifest): number {
  return totalBytes(modelAssets(manifest));
}

/**
 * A download size as a person deciding whether to spend it would write it.
 *
 * **Deliberately not this package's own `formatBytes`**, in `recovery.ts`, which
 * the storage figures beside a recovery offer use. That one is 1024-based with
 * `MB` labels, matching File Explorer, and it is right for what it does: somebody
 * reading it is about to go and look at the file in Windows, and a size that
 * disagreed with the one Windows showed them would read as a different file.
 *
 * Nobody is going to look at this in Explorer. It is a number in a sentence about
 * a connection, and a connection is sold in megabits per second — so the division
 * a visitor on a metered plan is about to do is by 1000, and quoting them 1024s
 * would make their arithmetic wrong by seven per cent. Different question,
 * different unit, different name.
 */
export function formatDownloadSize(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1_000)} kB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * A manifest as it came off the wire, checked and with every URL made absolute.
 *
 * Two hosts fetch this file and both have to react the same way to a deployment
 * that is not serving a model and to one whose manifest predates the runtime
 * entry — both of those reach a person as `unavailable.why`, on a screen beside a
 * prompter that still works (goal G5), so a second wording of either would be two
 * accounts of the same deployment.
 *
 * What is *not* shared is `resolve`, and that is the whole reason this takes it
 * as an argument: the browser resolves against the page it is running on and a
 * phone against the one origin it is allowed to talk to (ADR-0012), which is a
 * difference of hosting rather than of manifest. Every URL goes through it once,
 * here, because the loader uses them as store keys as well as fetch targets and
 * one resolved in two places is a file written under one name and looked for
 * under another.
 */
export function parseModelManifest(
  json: unknown,
  resolve: (url: string) => string,
): ModelManifest {
  const manifest = json as ModelManifest;
  // A manifest written before web ticket 15 has no runtime in it, and a
  // deployment serving one would otherwise fail somewhere much further along,
  // trying to fetch `undefined`.
  if (manifest.runtime?.url === undefined) {
    throw new Error("this deployment's speech model manifest is out of date.");
  }
  const absolute = (asset: ModelAsset): ModelAsset => ({
    ...asset,
    url: resolve(asset.url),
  });
  return {
    ...manifest,
    runtime: absolute(manifest.runtime),
    files: manifest.files.map((file) => ({ ...absolute(file), role: file.role })),
  };
}

/** Why a deployment has no model to offer, in the words both hosts show. */
export function noModelServed(status: number): string {
  return `this deployment is not serving a speech model (HTTP ${status}).`;
}
